import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import { cleanupThemeDomBeforeReactUpdate } from "./themeDomCleanup";

function appendScript(src, scriptGroup) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-theme-src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", (event) => reject(event), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = false;
    script.dataset.themeSrc = src;
    if (scriptGroup) {
      script.dataset.themeGroup = scriptGroup;
    }
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function removeScriptsByGroup(scriptGroup) {
  const scripts = document.querySelectorAll(`script[data-theme-group="${scriptGroup}"]`);
  scripts.forEach((script) => script.remove());
}

export default function ThemeScriptLoader() {
  const location = useLocation();
  const { locale } = useI18n();
  const pluginsLoadedRef = useRef(false);

  useEffect(() => {
    const loadThemeScripts = async () => {
      try {
        if (!pluginsLoadedRef.current) {
          await appendScript("/js/plugins.js", "theme-plugins");
          pluginsLoadedRef.current = true;
        }

        // Re-run theme initializers on route change so sliders/backgrounds work
        // when navigating back to pages like Home.
        removeScriptsByGroup("theme-main");
        cleanupThemeDomBeforeReactUpdate();
        const qs = new URLSearchParams({ route: location.pathname, ts: String(Date.now()) });
        await appendScript(`/js/main.js?${qs.toString()}`, "theme-main");
      } catch (error) {
        // Keep app alive even if a theme asset fails.
        // eslint-disable-next-line no-console
        console.error("Theme script failed to load:", error);
      }
    };

    // Let React paint page content before jQuery theme initialization.
    const timer = setTimeout(loadThemeScripts, 50);
    return () => clearTimeout(timer);
  }, [locale, location.pathname]);

  return null;
}
